<template>
    <TailwindLayout title="Pricing Features">
        <div class="-mx -mt-10 space-y-6">
            <!-- Header band -->
            <div class="relative overflow-hidden rounded-2xl border border-rent-risk-blue-100 bg-gradient-to-br from-rent-risk-blue-100 via-white to-white px-6 py-7 sm:px-8">
                <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div class="flex items-center gap-4">
                        <span class="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-rent-risk-blue-400 text-white shadow-sm">
                            <font-awesome-icon :icon="faTags" class="h-7 w-7" />
                        </span>
                        <div>
                            <h1 class="text-3xl font-bold tracking-tight text-rent-risk-blue-400">Pricing Features</h1>
                            <p class="mt-1 max-w-2xl text-sm text-gray-600">
                                The feature rows shown on the public pricing page. Add, edit, remove, and reorder
                                categories and features, and set each plan's cell to a checkmark, a dash, or custom text.
                            </p>
                        </div>
                    </div>
                    <div v-show="activeTab === 'features'" class="flex shrink-0 items-center gap-3">
                        <transition name="fade">
                            <span v-if="form.recentlySuccessful" class="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600">
                                <font-awesome-icon :icon="faCircleCheck" class="h-4 w-4" />
                                Saved
                            </span>
                        </transition>
                        <button
                            type="button"
                            :disabled="form.processing"
                            class="inline-flex items-center justify-center gap-2 rounded-lg bg-rent-risk-blue-400 px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
                            @click="save"
                        >
                            <font-awesome-icon :icon="faFloppyDisk" class="h-4 w-4" />
                            {{ form.processing ? 'Saving…' : 'Save changes' }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Tabs -->
            <div class="border-b border-gray-200">
                <nav class="-mb-px flex gap-6">
                    <button
                        type="button"
                        class="border-b-2 px-1 pb-3 text-sm font-semibold transition"
                        :class="activeTab === 'features' ? 'border-rent-risk-blue-400 text-rent-risk-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700'"
                        @click="activeTab = 'features'"
                    >
                        Feature Matrix
                    </button>
                    <button
                        type="button"
                        class="border-b-2 px-1 pb-3 text-sm font-semibold transition"
                        :class="activeTab === 'plans' ? 'border-rent-risk-blue-400 text-rent-risk-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700'"
                        @click="activeTab = 'plans'"
                    >
                        Plans &amp; Pricing
                    </button>
                </nav>
            </div>

            <!-- ===== Feature Matrix tab ===== -->
            <div v-show="activeTab === 'features'" class="space-y-6">

            <!-- Validation banner -->
            <div v-if="form.hasErrors" class="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-5">
                <font-awesome-icon :icon="faTriangleExclamation" class="mt-0.5 h-5 w-5 text-red-500" />
                <div>
                    <p class="text-sm font-semibold text-red-800">Some changes couldn't be saved.</p>
                    <p class="mt-0.5 text-xs text-red-700">Every category and feature needs a name. Check the highlighted fields and save again.</p>
                </div>
            </div>

            <!-- Legend -->
            <div class="flex flex-wrap items-center gap-x-6 gap-y-2 px-1 text-xs text-gray-500">
                <span class="font-semibold uppercase tracking-wider text-gray-400">Cell options</span>
                <span class="inline-flex items-center gap-1.5"><font-awesome-icon :icon="faCheck" class="h-3 w-3 text-green-500" /> Included</span>
                <span class="inline-flex items-center gap-1.5"><font-awesome-icon :icon="faMinus" class="h-3 w-3 text-gray-400" /> Not included</span>
                <span class="inline-flex items-center gap-1.5"><font-awesome-icon :icon="faFont" class="h-3 w-3 text-gray-400" /> Custom text (e.g. $49)</span>
            </div>

            <!-- Categories -->
            <div class="space-y-5">
                <div
                    v-for="(category, ci) in form.categories"
                    :key="category.key"
                    class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
                >
                    <!-- Category header -->
                    <div class="flex flex-col gap-3 border-b border-gray-100 bg-gray-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                        <div class="flex flex-1 items-center gap-3">
                            <input
                                v-model="category.name"
                                type="text"
                                placeholder="Category name"
                                class="w-full max-w-md rounded-lg border-gray-300 text-sm font-semibold text-gray-900 focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400"
                            />
                            <label class="inline-flex shrink-0 items-center gap-2 text-xs font-medium text-gray-600">
                                <input v-model="category.coming_soon" type="checkbox" class="rounded border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400" />
                                Coming soon
                            </label>
                        </div>
                        <div class="flex shrink-0 items-center gap-1">
                            <button type="button" title="Move up" :disabled="ci === 0" class="rounded-lg p-2 text-gray-400 transition hover:bg-gray-200 hover:text-gray-600 disabled:opacity-30" @click="moveCategory(ci, -1)">
                                <font-awesome-icon :icon="faArrowUp" class="h-3.5 w-3.5" />
                            </button>
                            <button type="button" title="Move down" :disabled="ci === form.categories.length - 1" class="rounded-lg p-2 text-gray-400 transition hover:bg-gray-200 hover:text-gray-600 disabled:opacity-30" @click="moveCategory(ci, 1)">
                                <font-awesome-icon :icon="faArrowDown" class="h-3.5 w-3.5" />
                            </button>
                            <button type="button" title="Delete category" class="rounded-lg p-2 text-red-400 transition hover:bg-red-50 hover:text-red-600" @click="removeCategory(ci)">
                                <font-awesome-icon :icon="faTrash" class="h-3.5 w-3.5" />
                            </button>
                        </div>
                    </div>

                    <!-- Features table -->
                    <div class="overflow-x-auto">
                        <table class="min-w-full text-sm">
                            <thead class="text-left text-xs font-semibold uppercase tracking-wider text-gray-400">
                                <tr>
                                    <th class="px-5 py-3">Feature</th>
                                    <th v-for="column in columns" :key="column.slug" class="px-4 py-3 text-center">{{ column.label }}</th>
                                    <th class="px-5 py-3 text-right">Order</th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-100">
                                <tr v-for="(feature, fi) in category.features" :key="feature.key" class="align-top hover:bg-gray-50/60">
                                    <td class="px-5 py-3">
                                        <input
                                            v-model="feature.name"
                                            type="text"
                                            placeholder="Feature name"
                                            class="w-full min-w-[14rem] rounded-lg border-gray-300 text-sm text-gray-900 focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400"
                                        />
                                        <label class="mt-2 inline-flex items-center gap-2 text-xs text-gray-500">
                                            <input v-model="feature.has_info" type="checkbox" class="rounded border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400" />
                                            Info icon
                                        </label>
                                        <input
                                            v-if="feature.has_info"
                                            v-model="feature.info_text"
                                            type="text"
                                            placeholder="Tooltip text shown on hover"
                                            class="mt-2 block w-full rounded-lg border-gray-300 text-xs text-gray-900 focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400"
                                        />
                                    </td>
                                    <td v-for="column in columns" :key="column.slug" class="px-4 py-3">
                                        <div class="flex flex-col items-center gap-1.5">
                                            <div class="inline-flex overflow-hidden rounded-lg border border-gray-200">
                                                <button
                                                    type="button"
                                                    title="Included"
                                                    class="flex h-7 w-7 items-center justify-center transition"
                                                    :class="feature.cells[column.slug].mode === 'check' ? 'bg-green-500 text-white' : 'bg-white text-gray-400 hover:bg-gray-100'"
                                                    @click="feature.cells[column.slug].mode = 'check'"
                                                >
                                                    <font-awesome-icon :icon="faCheck" class="h-3 w-3" />
                                                </button>
                                                <button
                                                    type="button"
                                                    title="Not included"
                                                    class="flex h-7 w-7 items-center justify-center border-l border-gray-200 transition"
                                                    :class="feature.cells[column.slug].mode === 'dash' ? 'bg-gray-500 text-white' : 'bg-white text-gray-400 hover:bg-gray-100'"
                                                    @click="feature.cells[column.slug].mode = 'dash'"
                                                >
                                                    <font-awesome-icon :icon="faMinus" class="h-3 w-3" />
                                                </button>
                                                <button
                                                    type="button"
                                                    title="Custom text"
                                                    class="flex h-7 w-7 items-center justify-center border-l border-gray-200 transition"
                                                    :class="feature.cells[column.slug].mode === 'text' ? 'bg-rent-risk-blue-400 text-white' : 'bg-white text-gray-400 hover:bg-gray-100'"
                                                    @click="feature.cells[column.slug].mode = 'text'"
                                                >
                                                    <font-awesome-icon :icon="faFont" class="h-3 w-3" />
                                                </button>
                                            </div>
                                            <input
                                                v-if="feature.cells[column.slug].mode === 'text'"
                                                v-model="feature.cells[column.slug].text"
                                                type="text"
                                                placeholder="$49"
                                                class="w-20 rounded-md border-gray-300 px-2 py-1 text-center text-xs focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400"
                                            />
                                        </div>
                                    </td>
                                    <td class="px-5 py-3">
                                        <div class="flex items-center justify-end gap-1">
                                            <button type="button" title="Move up" :disabled="fi === 0" class="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-200 hover:text-gray-600 disabled:opacity-30" @click="moveFeature(category, fi, -1)">
                                                <font-awesome-icon :icon="faArrowUp" class="h-3 w-3" />
                                            </button>
                                            <button type="button" title="Move down" :disabled="fi === category.features.length - 1" class="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-200 hover:text-gray-600 disabled:opacity-30" @click="moveFeature(category, fi, 1)">
                                                <font-awesome-icon :icon="faArrowDown" class="h-3 w-3" />
                                            </button>
                                            <button type="button" title="Delete feature" class="rounded-lg p-1.5 text-red-400 transition hover:bg-red-50 hover:text-red-600" @click="category.features.splice(fi, 1)">
                                                <font-awesome-icon :icon="faTrash" class="h-3 w-3" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                <tr v-if="category.features.length === 0">
                                    <td :colspan="columns.length + 2" class="px-5 py-6 text-center text-xs text-gray-400">
                                        No features yet — add the first one below.
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="border-t border-gray-100 px-5 py-3">
                        <button type="button" class="inline-flex items-center gap-1.5 text-sm font-medium text-rent-risk-blue-400 transition hover:opacity-80" @click="addFeature(category)">
                            <font-awesome-icon :icon="faPlus" class="h-3.5 w-3.5" />
                            Add feature
                        </button>
                    </div>
                </div>
            </div>

            <!-- Add category -->
            <button
                type="button"
                class="inline-flex items-center gap-2 rounded-xl border border-dashed border-gray-300 px-5 py-3 text-sm font-semibold text-gray-600 transition hover:border-rent-risk-blue-400 hover:text-rent-risk-blue-400"
                @click="addCategory"
            >
                <font-awesome-icon :icon="faPlus" class="h-4 w-4" />
                Add category
            </button>
            </div>
            <!-- ===== /Feature Matrix tab ===== -->

            <!-- ===== Plans & Pricing tab ===== -->
            <div v-show="activeTab === 'plans'" class="space-y-5">
                <!-- Billing-safety guidance -->
                <div class="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5">
                    <font-awesome-icon :icon="faTriangleExclamation" class="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                    <div class="text-sm text-amber-800">
                        <p class="font-semibold">The price is what shows; the Stripe Price ID is what charges.</p>
                        <p class="mt-0.5 text-xs text-amber-700">
                            To change an amount, create a new Price in Stripe, then paste its Price ID and the matching
                            amount here. We verify the two match before saving — you can't save a plan whose displayed
                            price differs from what Stripe actually charges. Slug, type, and billing cycle are locked.
                        </p>
                    </div>
                </div>

                <div
                    v-for="plan in plans"
                    :key="plan.id"
                    class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
                >
                    <!-- Locked identity + save -->
                    <div class="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-3">
                        <div class="flex flex-wrap items-center gap-2">
                            <span class="rounded bg-gray-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-gray-500">{{ plan.type }}</span>
                            <span class="font-mono text-xs text-gray-500">{{ plan.slug }}</span>
                            <span class="rounded-full bg-rent-risk-blue-100 px-2 py-0.5 text-xs font-medium text-rent-risk-blue-400">{{ plan.billing_cycle }}</span>
                        </div>
                        <div class="flex items-center gap-3">
                            <transition name="fade">
                                <span v-if="planSaved[plan.id]" class="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                                    <font-awesome-icon :icon="faCircleCheck" class="h-4 w-4" />
                                    Saved
                                </span>
                            </transition>
                            <button
                                type="button"
                                :disabled="planSaving[plan.id]"
                                class="inline-flex items-center gap-2 rounded-lg bg-rent-risk-blue-400 px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
                                @click="savePlan(plan)"
                            >
                                <font-awesome-icon :icon="faFloppyDisk" class="h-4 w-4" />
                                {{ planSaving[plan.id] ? 'Saving…' : 'Save plan' }}
                            </button>
                        </div>
                    </div>

                    <div class="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        <label class="block">
                            <span class="text-xs font-semibold uppercase tracking-wider text-gray-400">Display name</span>
                            <input v-model="plan.name" type="text" class="mt-1 w-full rounded-lg border-gray-300 text-sm focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400" />
                            <span v-if="planErrors[plan.id]?.name" class="mt-1 block text-xs text-red-600">{{ planErrors[plan.id].name }}</span>
                        </label>
                        <label class="block">
                            <span class="text-xs font-semibold uppercase tracking-wider text-gray-400">Price (USD)</span>
                            <input v-model="plan.price" type="text" inputmode="decimal" class="mt-1 w-full rounded-lg border-gray-300 text-sm focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400" />
                            <span v-if="planErrors[plan.id]?.price" class="mt-1 block text-xs text-red-600">{{ planErrors[plan.id].price }}</span>
                        </label>
                        <label class="block">
                            <span class="text-xs font-semibold uppercase tracking-wider text-gray-400">Stripe Price ID</span>
                            <input v-model="plan.stripe_price_id" type="text" class="mt-1 w-full rounded-lg border-gray-300 font-mono text-xs focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400" />
                            <span v-if="planErrors[plan.id]?.stripe_price_id" class="mt-1 block text-xs text-red-600">{{ planErrors[plan.id].stripe_price_id }}</span>
                        </label>
                        <label class="block sm:col-span-2 lg:col-span-3">
                            <span class="text-xs font-semibold uppercase tracking-wider text-gray-400">Description</span>
                            <input v-model="plan.description" type="text" class="mt-1 w-full rounded-lg border-gray-300 text-sm focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400" />
                        </label>
                        <label class="block">
                            <span class="text-xs font-semibold uppercase tracking-wider text-gray-400">Annual savings %</span>
                            <input v-model="plan.annual_savings_percentage" type="text" inputmode="decimal" placeholder="—" class="mt-1 w-full rounded-lg border-gray-300 text-sm focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400" />
                        </label>
                        <label class="block">
                            <span class="text-xs font-semibold uppercase tracking-wider text-gray-400">Trial days</span>
                            <input v-model.number="plan.trial_days" type="number" min="0" class="mt-1 w-full rounded-lg border-gray-300 text-sm focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400" />
                        </label>
                        <label class="block">
                            <span class="text-xs font-semibold uppercase tracking-wider text-gray-400">Sort order</span>
                            <input v-model.number="plan.sort_order" type="number" min="0" class="mt-1 w-full rounded-lg border-gray-300 text-sm focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400" />
                        </label>
                        <div class="flex items-center gap-6 pt-6">
                            <label class="inline-flex items-center gap-2 text-sm text-gray-600">
                                <input v-model="plan.is_popular" type="checkbox" class="rounded border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400" />
                                Popular
                            </label>
                            <label class="inline-flex items-center gap-2 text-sm text-gray-600">
                                <input v-model="plan.is_active" type="checkbox" class="rounded border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400" />
                                Active
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </TailwindLayout>
</template>

<script setup>
import { ref } from 'vue'
import { router, useForm } from '@inertiajs/vue3'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
    faTags,
    faCheck,
    faMinus,
    faFont,
    faPlus,
    faTrash,
    faArrowUp,
    faArrowDown,
    faFloppyDisk,
    faCircleCheck,
    faTriangleExclamation,
} from '@fortawesome/pro-light-svg-icons'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'

const props = defineProps({
    categories: { type: Array, default: () => [] },
    columns: { type: Array, default: () => [] },
    plans: { type: Array, default: () => [] },
})

const activeTab = ref('features')

// Plans tab: per-plan editable copies + per-plan save state (keyed by plan id).
const plans = ref(props.plans.map((plan) => ({ ...plan })))
const planErrors = ref({})
const planSaving = ref({})
const planSaved = ref({})

const savePlan = (plan) => {
    planSaving.value[plan.id] = true
    planErrors.value[plan.id] = {}
    planSaved.value[plan.id] = false
    router.put(route('bridge.pricing-features.plans.update', plan.id), {
        name: plan.name,
        description: plan.description,
        price: plan.price,
        stripe_price_id: plan.stripe_price_id,
        is_popular: plan.is_popular,
        is_active: plan.is_active,
        sort_order: plan.sort_order,
        annual_savings_percentage: plan.annual_savings_percentage,
        trial_days: plan.trial_days,
    }, {
        preserveScroll: true,
        preserveState: true,
        onError: (errors) => { planErrors.value[plan.id] = errors },
        onSuccess: () => {
            planSaved.value[plan.id] = true
            setTimeout(() => { planSaved.value[plan.id] = false }, 2500)
        },
        onFinish: () => { planSaving.value[plan.id] = false },
    })
}

// Client-only stable keys for v-for, so reordering/adding rows never reuses a
// stale DOM node. Not sent to the server.
let uid = 0
const nextKey = () => `row-${uid++}`

// A stored cell value (true | false | string) -> the editor's {mode, text} shape.
const toCell = (value) => {
    if (value === true) {
        return { mode: 'check', text: '' }
    }
    if (typeof value === 'string' && value.trim() !== '') {
        return { mode: 'text', text: value }
    }
    return { mode: 'dash', text: '' }
}

// The editor shape -> the value the server stores.
const fromCell = (cell) => {
    if (cell.mode === 'check') {
        return true
    }
    if (cell.mode === 'text') {
        return cell.text.trim() === '' ? false : cell.text
    }
    return false
}

const buildCells = (values = {}) => {
    const cells = {}
    props.columns.forEach((column) => {
        cells[column.slug] = toCell(values?.[column.slug])
    })
    return cells
}

const buildFeature = (feature = {}) => ({
    key: nextKey(),
    id: feature.id ?? null,
    name: feature.name ?? '',
    has_info: Boolean(feature.has_info),
    info_text: feature.info_text ?? '',
    cells: buildCells(feature.values),
})

const buildCategory = (category = {}) => ({
    key: nextKey(),
    id: category.id ?? null,
    name: category.name ?? '',
    coming_soon: Boolean(category.coming_soon),
    features: (category.features ?? []).map(buildFeature),
})

const form = useForm({
    categories: props.categories.map(buildCategory),
})

const addCategory = () => {
    form.categories.push(buildCategory({ features: [{}] }))
}

const removeCategory = (index) => {
    form.categories.splice(index, 1)
}

const addFeature = (category) => {
    category.features.push(buildFeature())
}

const move = (list, index, delta) => {
    const target = index + delta
    if (target < 0 || target >= list.length) {
        return
    }
    const [item] = list.splice(index, 1)
    list.splice(target, 0, item)
}

const moveCategory = (index, delta) => move(form.categories, index, delta)
const moveFeature = (category, index, delta) => move(category.features, index, delta)

const save = () => {
    form
        .transform((data) => ({
            categories: data.categories.map((category) => ({
                id: category.id,
                name: category.name,
                coming_soon: category.coming_soon,
                features: category.features.map((feature) => {
                    const values = {}
                    props.columns.forEach((column) => {
                        values[column.slug] = fromCell(feature.cells[column.slug])
                    })
                    return {
                        id: feature.id,
                        name: feature.name,
                        has_info: feature.has_info,
                        info_text: feature.info_text,
                        values,
                    }
                }),
            })),
        }))
        .put(route('bridge.pricing-features.sync'), {
            preserveScroll: true,
        })
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
